import { Injectable } from '@angular/core';
import { HttpClientService } from '../common/http-client.service';

export interface MenuItem {
  name: string;
  icon?: string;
  url?: string;
  router_url?: string;
  routerURL?: string;
  show: boolean;
  force_reload?: boolean;
  forceReload?: boolean;
  target?: string;
  navbar_sidekiq?: boolean;
  clipboard?: boolean;
  children?: MenuItem[];
}

export interface NavbarResponse {
  menu_items: MenuItem[];
  secondary_menus: {
    accounting: MenuItem[];
    matter: MenuItem[] | null;
    home: MenuItem[];
  };
  intranet: {
    id: number;
    name: string;
    short_name: string;
    navbar_label: string;
  };
  user: {
    id: number;
    name: string;
    login: string;
    person_id: number;
  };
  attorneys: Array<{ id: number; name: string }>;
  user_groups: string[];
  linked_intranets: Array<{ label: string; value: number }>;
  switchable_users: Array<{ id: number; name: string; login: string }>;
  user_switch: { active: boolean; original_user: string | null };
  navbar_data: {
    user_id: number;
    intranet_id: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class NavbarService {
  private url = 'navbar';
  private cachedMenu: NavbarResponse | null = null;

  constructor(private httpClient: HttpClientService) {}

  /**
   * Fetch the navbar menu from the API
   * @param forceRefresh If true, bypass cache and fetch fresh data
   */
  getMenu(forceRefresh: boolean = false): Promise<NavbarResponse> {
    if (this.cachedMenu && !forceRefresh) {
      return Promise.resolve(this.cachedMenu);
    }

    return this.httpClient.get(`${this.url}/menu.json`).then((response: NavbarResponse) => {
      // Process URLs to replace placeholders
      this.processMenuUrls(response.menu_items, response.navbar_data);
      if (response.secondary_menus) {
        if (response.secondary_menus.accounting) {
          this.processMenuUrls(response.secondary_menus.accounting, response.navbar_data);
        }
        if (response.secondary_menus.home) {
          this.processMenuUrls(response.secondary_menus.home, response.navbar_data);
        }
      }
      // Transform snake_case keys to camelCase for Angular templates
      this.transformKeys(response.menu_items);
      if (response.secondary_menus) {
        if (response.secondary_menus.accounting) {
          this.transformKeys(response.secondary_menus.accounting);
        }
        if (response.secondary_menus.home) {
          this.transformKeys(response.secondary_menus.home);
        }
      }
      this.cachedMenu = response;
      return response;
    });
  }

  /**
   * Clear the cached menu
   */
  clearCache(): void {
    this.cachedMenu = null;
  }

  /**
   * Transform snake_case keys from Rails API to camelCase for Angular templates.
   * Converts router_url -> routerURL, force_reload -> forceReload
   */
  private transformKeys(items: MenuItem[]): void {
    if (!items) return;

    for (const item of items) {
      if (item.router_url !== undefined) {
        item.routerURL = item.router_url;
        delete item.router_url;
      }
      if (item.force_reload !== undefined) {
        item.forceReload = item.force_reload;
        delete item.force_reload;
      }
      if (item.children) {
        this.transformKeys(item.children);
      }
    }
  }

  /**
   * Process menu items to replace URL placeholders with actual values
   */
  private processMenuUrls(items: MenuItem[], navbarData: { user_id: number; intranet_id: number }): void {
    if (!items) return;

    for (const item of items) {
      if (item.url) {
        item.url = this.replacePlaceholders(item.url, navbarData);
      }
      if (item.router_url) {
        item.router_url = this.replacePlaceholders(item.router_url, navbarData);
      }
      if (item.children) {
        this.processMenuUrls(item.children, navbarData);
      }
    }
  }

  /**
   * Replace :user_id and :intranet_id placeholders in URLs
   */
  private replacePlaceholders(url: string, navbarData: { user_id: number; intranet_id: number }): string {
    return url
      .replace(':user_id', String(navbarData.user_id))
      .replace(':intranet_id', String(navbarData.intranet_id));
  }
}
